// =============================================================================
// feitos.ts — Motor ÚNICO de feitos por jogador (issues #99, #92 e #95):
// a simulação rastreia os feitos da partida (MatchFeats), o perfil acumula
// as estatísticas vitalícias (FeatsStats) e TRÊS faces leem o mesmo motor:
// - CONQUISTAS "Promessas de Campanha" (#99): condições variadas, recompensa
//   em votos vitalícios, armas do pool da urna e hooks de skin (issue #24).
// - DOSSIÊS (#92): 3 missões persistentes sempre parcialmente completas.
// - ARQUIVO DA CPI (#95): bestiário com primeira-vista e kills por tipo.
// Só DADOS e funções puras: a avaliação autoritativa roda no servidor durante
// a partida e a persistência mora em Db.ts (logado) ou no localStorage do
// navegador (anônimo/modo dev), sempre pelas MESMAS funções deste arquivo.
// =============================================================================
import { BOSSES, ENEMIES, ETYPE_IDS } from './enemies';
import { WEAPON_TABLE } from './weapons';

// -----------------------------------------------------------------------------
// Estatísticas vitalícias por conta (a fonte de verdade das três faces)
// -----------------------------------------------------------------------------
export interface FeatsStats {
  kills: number;                          // adversários neutralizados na vida
  killsPorArma: Record<string, number>;   // WeaponId -> abates com ela
  killsPorTipo: Record<string, number>;   // tipo de inimigo/boss -> abates (Arquivo da CPI)
  bosses: Record<string, number>;         // BossId -> vezes arquivado (crédito da chapa)
  maletas: number;                        // Cassinos da Maleta abertos
  revives: number;                        // aliados reanimados
  zumbificacoes: number;                  // vezes que virou zumbi político
  congelados: number;                     // inimigos congelados (Decisão do STF)
  evolucoes: number;                      // armas evoluídas ao nível máximo
  votos: number;                          // votos coletados na vida (contador do motor)
  tempo: number;                          // segundos de campanha acumulados
  partidas: number;
  vitorias: number;
  vitoriasDificil: number;                // vitórias na Apuração Final
  melhorTempo: number;                    // recorde de segundos numa run
  melhorScore: number;                    // recorde de pontos numa run
  melhorNivel: number;                    // recorde de nível da chapa numa run
  infinitoTempo: number;                  // recorde de segundos ALÉM dos 20min no infinito
  vistos: string[];                       // tipos já encontrados (primeira-vista do Arquivo)
}

export function freshFeatsStats(): FeatsStats {
  return {
    kills: 0, killsPorArma: {}, killsPorTipo: {}, bosses: {},
    maletas: 0, revives: 0, zumbificacoes: 0, congelados: 0, evolucoes: 0,
    votos: 0, tempo: 0, partidas: 0, vitorias: 0, vitoriasDificil: 0,
    melhorTempo: 0, melhorScore: 0, melhorNivel: 0, infinitoTempo: 0, vistos: [],
  };
}

// -----------------------------------------------------------------------------
// Feitos de UMA participação em partida (o delta que viaja pro perfil).
// Contadores somam; os campos "run*" e infinitoTempo são ABSOLUTOS da run e
// fundem por máximo; vistos funde por união (idempotente).
// -----------------------------------------------------------------------------
export interface MatchFeats {
  kills: number;
  killsPorArma: Record<string, number>;
  killsPorTipo: Record<string, number>;
  bosses: Record<string, number>;
  maletas: number;
  revives: number;
  zumbificacoes: number;
  congelados: number;
  evolucoes: number;
  votos: number;
  tempo: number;
  partidas: number;
  vitorias: number;
  vitoriasDificil: number;
  runTempo: number;
  runScore: number;
  runNivel: number;
  infinitoTempo: number;
  vistos: string[];
}

export function emptyMatchFeats(): MatchFeats {
  return {
    kills: 0, killsPorArma: {}, killsPorTipo: {}, bosses: {},
    maletas: 0, revives: 0, zumbificacoes: 0, congelados: 0, evolucoes: 0,
    votos: 0, tempo: 0, partidas: 0, vitorias: 0, vitoriasDificil: 0,
    runTempo: 0, runScore: 0, runNivel: 0, infinitoTempo: 0, vistos: [],
  };
}

// Delta entre o acumulado atual e o último envio (crédito incremental: seguir
// pro modo infinito depois da apuração vencida não conta feito em dobro)
export function diffMatchFeats(cur: MatchFeats, sent: MatchFeats | null): MatchFeats {
  if (!sent) return cur;
  const rec = (c: Record<string, number>, s: Record<string, number>): Record<string, number> => {
    const out: Record<string, number> = {};
    for (const [k, v] of Object.entries(c)) {
      const d = v - (s[k] ?? 0);
      if (d > 0) out[k] = d;
    }
    return out;
  };
  return {
    kills: Math.max(0, cur.kills - sent.kills),
    killsPorArma: rec(cur.killsPorArma, sent.killsPorArma),
    killsPorTipo: rec(cur.killsPorTipo, sent.killsPorTipo),
    bosses: rec(cur.bosses, sent.bosses),
    maletas: Math.max(0, cur.maletas - sent.maletas),
    revives: Math.max(0, cur.revives - sent.revives),
    zumbificacoes: Math.max(0, cur.zumbificacoes - sent.zumbificacoes),
    congelados: Math.max(0, cur.congelados - sent.congelados),
    evolucoes: Math.max(0, cur.evolucoes - sent.evolucoes),
    votos: Math.max(0, cur.votos - sent.votos),
    tempo: Math.max(0, cur.tempo - sent.tempo),
    partidas: Math.max(0, cur.partidas - sent.partidas),
    vitorias: Math.max(0, cur.vitorias - sent.vitorias),
    vitoriasDificil: Math.max(0, cur.vitoriasDificil - sent.vitoriasDificil),
    // Absolutos: sempre o valor cheio da run (o perfil funde por máximo/união)
    runTempo: cur.runTempo,
    runScore: cur.runScore,
    runNivel: cur.runNivel,
    infinitoTempo: cur.infinitoTempo,
    vistos: cur.vistos,
  };
}

// Funde os feitos de uma participação nas estatísticas vitalícias (mutável)
export function applyMatchToStats(stats: FeatsStats, m: MatchFeats): void {
  const rec = (into: Record<string, number>, from: Record<string, number>): void => {
    for (const [k, v] of Object.entries(from)) into[k] = (into[k] ?? 0) + v;
  };
  stats.kills += m.kills;
  rec(stats.killsPorArma, m.killsPorArma);
  rec(stats.killsPorTipo, m.killsPorTipo);
  rec(stats.bosses, m.bosses);
  stats.maletas += m.maletas;
  stats.revives += m.revives;
  stats.zumbificacoes += m.zumbificacoes;
  stats.congelados += m.congelados;
  stats.evolucoes += m.evolucoes;
  stats.votos += m.votos;
  stats.tempo += m.tempo;
  stats.partidas += m.partidas;
  stats.vitorias += m.vitorias;
  stats.vitoriasDificil += m.vitoriasDificil;
  stats.melhorTempo = Math.max(stats.melhorTempo, m.runTempo);
  stats.melhorScore = Math.max(stats.melhorScore, m.runScore);
  stats.melhorNivel = Math.max(stats.melhorNivel, m.runNivel);
  stats.infinitoTempo = Math.max(stats.infinitoTempo, m.infinitoTempo);
  for (const t of m.vistos) {
    if (ETYPE_IDS.includes(t) && !stats.vistos.includes(t)) stats.vistos.push(t);
  }
}

// -----------------------------------------------------------------------------
// CONQUISTAS — "Promessas de Campanha" (#99): avaliadas no servidor durante a
// partida (toast) e no crédito do perfil (recompensa). O progresso é sempre
// uma leitura das estatísticas vitalícias — o motor é um só.
// -----------------------------------------------------------------------------
export interface ConquistaDef {
  id: string;
  nome: string;
  desc: string;                           // a promessa, em juridiquês de palanque
  alvo: number;
  progress: (s: FeatsStats) => number;
  votos: number;                          // recompensa em votos vitalícios
  arma?: string;                          // WeaponId destravada no pool da urna
  skin?: string;                          // hook de skin (issue #24 — sprite em outro PR)
  formato?: 'num' | 'tempo';              // exibição do progresso no Diário Oficial
}

const distintos = (r: Record<string, number>): number =>
  Object.values(r).filter((v) => v > 0).length;

export const TOTAL_BOSSES = Object.keys(BOSSES).length;
export const TOTAL_ARQUIVO = ETYPE_IDS.length;

export const CONQUISTAS = {
  boca_de_urna: {
    id: 'boca_de_urna', nome: 'Boca de Urna',
    desc: 'Dispute sua primeira eleição. Todo mundo começa distribuindo santinho.',
    alvo: 1, progress: (s) => s.partidas, votos: 200,
  },
  militante: {
    id: 'militante', nome: 'Militante de Carteirinha',
    desc: 'Dispute 3 eleições. O churrasco do comitê já te conhece pelo nome.',
    alvo: 3, progress: (s) => s.partidas, votos: 250, arma: 'fake_news',
  },
  cabo_eleitoral: {
    id: 'cabo_eleitoral', nome: 'Cabo Eleitoral Vitalício',
    desc: 'Dispute 15 eleições. A essa altura a urna já te chama de "chefia".',
    alvo: 15, progress: (s) => s.partidas, votos: 600,
  },
  meia_apuracao: {
    id: 'meia_apuracao', nome: 'Metade da Apuração',
    desc: 'Sobreviva 10 minutos numa mesma eleição sem pedir asilo político.',
    alvo: 600, progress: (s) => s.melhorTempo, votos: 300, arma: 'pix_emendas', formato: 'tempo',
  },
  diplomado: {
    id: 'diplomado', nome: 'Diplomado pelo TSE',
    desc: 'Vença uma campanha: sobreviva os 20 minutos até o fim da apuração.',
    alvo: 1, progress: (s) => s.vitorias, votos: 800, arma: 'mensalao',
  },
  cassacao_500: {
    id: 'cassacao_500', nome: 'Cassação em Massa',
    desc: 'Neutralize 500 adversários políticos. Com recurso negado em todas as instâncias.',
    alvo: 500, progress: (s) => s.kills, votos: 400, arma: 'rachadinha',
  },
  rolo_compressor: {
    id: 'rolo_compressor', nome: 'Rolo Compressor do Centrão',
    desc: 'Neutralize 5000 adversários. A base aliada aprova qualquer pauta sua.',
    alvo: 5000, progress: (s) => s.kills, votos: 1000,
  },
  cala_boca: {
    id: 'cala_boca', nome: 'Cala Boca Já Morreu',
    desc: 'Arquive a Repórter da Globo. O ao vivo foi cortado.',
    alvo: 1, progress: (s) => s.bosses.live_19h ?? 0, votos: 300,
  },
  relatorio_final: {
    id: 'relatorio_final', nome: 'Relatório Aprovado no Plenário',
    desc: 'Arquive a Janja antes que ela organize a próxima posse.',
    alvo: 1, progress: (s) => s.bosses.cpi_covid ?? 0, votos: 400,
  },
  vidraceiro: {
    id: 'vidraceiro', nome: 'Amigo do Vidraceiro',
    desc: 'Arquive o Donald Trump. Alguém precisa pagar os vidros do Capitólio.',
    alvo: 1, progress: (s) => s.bosses.oito_jan_invasao ?? 0, votos: 800, skin: 'presidiario',
  },
  colecionador: {
    id: 'colecionador', nome: 'Colecionador de Escândalos',
    desc: `Arquive todos os ${TOTAL_BOSSES} chefes de escândalo ao menos uma vez.`,
    alvo: TOTAL_BOSSES, progress: (s) => distintos(s.bosses), votos: 1500, skin: 'vampiro',
  },
  amigo_da_mala: {
    id: 'amigo_da_mala', nome: 'Amigo da Mala',
    desc: 'Abra 25 Cassinos da Maleta. Era tudo doação não contabilizada.',
    alvo: 25, progress: (s) => s.maletas, votos: 400,
  },
  enfermeiro: {
    id: 'enfermeiro', nome: 'Enfermeiro da Coligação',
    desc: 'Reanime aliados caídos 10 vezes. O SUS da chapa é você.',
    alvo: 10, progress: (s) => s.revives, votos: 500,
  },
  radicalizado: {
    id: 'radicalizado', nome: 'Radicalizado no Grupo da Família',
    desc: 'Vire zumbi político 3 vezes. O algoritmo agradece o engajamento.',
    alvo: 3, progress: (s) => s.zumbificacoes, votos: 200,
  },
  sigilo_100: {
    id: 'sigilo_100', nome: 'Sigilo de 100 Anos',
    desc: 'Congele 100 inimigos com a Decisão do STF. Motivo? Sob sigilo.',
    alvo: 100, progress: (s) => s.congelados, votos: 500,
  },
  emenda_pec: {
    id: 'emenda_pec', nome: 'PEC da Arma Definitiva',
    desc: 'Evolua uma arma ao nível máximo. Aprovada em dois turnos, como manda a Constituição.',
    alvo: 1, progress: (s) => s.evolucoes, votos: 300,
  },
  puxador: {
    id: 'puxador', nome: 'Puxador de Legenda',
    desc: 'Leve a chapa ao nível 30 numa mesma eleição. Elege até poste.',
    alvo: 30, progress: (s) => s.melhorNivel, votos: 400,
  },
  vitalicio: {
    id: 'vitalicio', nome: 'Mandato Vitalício',
    desc: 'Sobreviva 5 minutos no MODO INFINITO. A reeleição virou cláusula pétrea.',
    alvo: 300, progress: (s) => s.infinitoTempo, votos: 700, formato: 'tempo',
  },
  prova_dos_nove: {
    id: 'prova_dos_nove', nome: 'Blindado pelo Foro',
    desc: 'Vença uma campanha na dificuldade Apuração Final. Nem a PF te alcança.',
    alvo: 1, progress: (s) => s.vitoriasDificil, votos: 1000, skin: 'blindado',
  },
  cpi_completa: {
    id: 'cpi_completa', nome: 'Arquivo Completo da CPI',
    desc: `Encontre todos os ${TOTAL_ARQUIVO} investigados do Arquivo da CPI em campo.`,
    alvo: TOTAL_ARQUIVO, progress: (s) => s.vistos.length, votos: 600,
  },
} satisfies Record<string, ConquistaDef>;

export type ConquistaId = keyof typeof CONQUISTAS;

export const CONQUISTA_LIST: ConquistaDef[] = Object.values(CONQUISTAS);

// Lookup por id vindo da rede (string qualquer)
export const CONQUISTA_TABLE: Record<string, ConquistaDef | undefined> = CONQUISTAS;

// Armas do pool da urna travadas por conquista (as que os marcos de votos do
// Curral Eleitoral em shared/meta.ts NÃO travam). O Faz o L e o #FiqueEmCasa
// seguem livres: urna de conta nova não pode nascer vazia.
export const CONQUISTA_ARMA_LOCKS: Record<string, string> = {};
for (const c of CONQUISTA_LIST) {
  if (c.arma) CONQUISTA_ARMA_LOCKS[c.arma] = c.id;
}

export function isWeaponUnlockedByConquistas(
  conquistas: ReadonlySet<string> | readonly string[], weaponId: string,
): boolean {
  const lock = CONQUISTA_ARMA_LOCKS[weaponId];
  if (!lock) return true;
  return Array.isArray(conquistas)
    ? conquistas.includes(lock)
    : (conquistas as ReadonlySet<string>).has(lock);
}

// -----------------------------------------------------------------------------
// DOSSIÊS (#92): 3 missões ativas, sempre parcialmente completas. Completou,
// o próximo nasce herdando o progresso da própria run (efeito Zeigarnik).
// -----------------------------------------------------------------------------
export interface DossieDef {
  id: string;
  nome: (alvo: number) => string;         // título carimbado na pasta
  alvoBase: number;                       // alvo do nível 0; escala linear por nível
  metric: (m: MatchFeats) => number;      // contribuição de uma participação
  formato?: 'num' | 'tempo';
}

export const DOSSIES = {
  neutralizar: {
    id: 'neutralizar',
    nome: (n: number) => `Neutralize ${n} adversários políticos`,
    alvoBase: 250,
    metric: (m: MatchFeats) => m.kills,
  },
  votos: {
    id: 'votos',
    nome: (n: number) => `Colete ${n} votos nas ruas`,
    alvoBase: 800,
    metric: (m: MatchFeats) => m.votos,
  },
  maletas: {
    id: 'maletas',
    nome: (n: number) => `Abra ${n} Cassinos da Maleta`,
    alvoBase: 6,
    metric: (m: MatchFeats) => m.maletas,
  },
  tempo: {
    id: 'tempo',
    nome: (n: number) => `Sobreviva ${Math.round(n / 60)} minutos em campanha`,
    alvoBase: 1800,
    metric: (m: MatchFeats) => m.tempo,
    formato: 'tempo',
  },
  bosses: {
    id: 'bosses',
    nome: (n: number) => `Arquive ${n} chefes de escândalo`,
    alvoBase: 4,
    metric: (m: MatchFeats) => Object.values(m.bosses).reduce((a, b) => a + b, 0),
  },
} satisfies Record<string, DossieDef>;

export const DOSSIE_LIST: DossieDef[] = Object.values(DOSSIES);
export const DOSSIE_TABLE: Record<string, DossieDef | undefined> = DOSSIES;

export const DOSSIES_ATIVOS = 3;               // pastas abertas em cima da mesa
export const DOSSIE_DOTE_FRAC = 0.1;           // pasta nova já nasce com 10% carimbado
export const DOSSIE_VOTOS_BASE = 150;          // recompensa do nível 0
export const DOSSIE_VOTOS_POR_NIVEL = 75;      // cresce com o nível do dossiê

export interface DossieState {
  tpl: string;        // id do template (DOSSIES)
  nivel: number;      // quantas vezes esse template já foi concluído
  alvo: number;
  prog: number;
}

export function dossieAlvo(def: DossieDef, nivel: number): number {
  return def.alvoBase * (nivel + 1);
}

export function dossieVotos(nivel: number): number {
  return DOSSIE_VOTOS_BASE + nivel * DOSSIE_VOTOS_POR_NIVEL;
}

function novoDossie(def: DossieDef, nivel: number, herdado: number): DossieState {
  const alvo = dossieAlvo(def, nivel);
  const dote = Math.ceil(alvo * DOSSIE_DOTE_FRAC);
  // Nunca nasce zerado (dote) e nunca nasce concluído (teto em alvo-1)
  const prog = Math.min(Math.max(herdado, dote), alvo - 1);
  return { tpl: def.id, nivel, alvo, prog };
}

export function freshDossies(): DossieState[] {
  return DOSSIE_LIST.slice(0, DOSSIES_ATIVOS).map((def) => novoDossie(def, 0, 0));
}

export interface DossieConcluido {
  nome: string;
  votos: number;
}

// Aplica a contribuição de uma participação aos 3 dossiês ativos (mutável).
// Concluiu? Recompensa em votos e a vaga recebe o template MENOS visto que não
// está na mesa — já nascendo com o progresso que ESTA run daria a ele.
export function applyMatchToDossies(
  state: FeitosState, m: MatchFeats,
): { votosBonus: number; concluidos: DossieConcluido[] } {
  let votosBonus = 0;
  const concluidos: DossieConcluido[] = [];
  for (let i = 0; i < state.dossies.length; i++) {
    const d = state.dossies[i];
    const def = DOSSIE_TABLE[d.tpl];
    if (!def) continue;
    d.prog += Math.max(0, Math.floor(def.metric(m)));
    if (d.prog < d.alvo) continue;
    // Carimbo de CONCLUÍDO: paga os votos e puxa a próxima pasta da pilha
    const ganho = dossieVotos(d.nivel);
    votosBonus += ganho;
    concluidos.push({ nome: def.nome(d.alvo), votos: ganho });
    state.nivel[d.tpl] = (state.nivel[d.tpl] ?? 0) + 1;
    const naMesa = new Set(state.dossies.map((s) => s.tpl));
    naMesa.delete(d.tpl);
    const candidatos = DOSSIE_LIST.filter((t) => !naMesa.has(t.id));
    const prox = candidatos.reduce((a, b) =>
      (state.nivel[a.id] ?? 0) <= (state.nivel[b.id] ?? 0) ? a : b);
    // Herança da run: a MESMA participação já contribui pra pasta nova
    state.dossies[i] = novoDossie(prox, state.nivel[prox.id] ?? 0,
      Math.max(0, Math.floor(prox.metric(m))));
  }
  return { votosBonus, concluidos };
}

// -----------------------------------------------------------------------------
// Estado completo de feitos que viaja no perfil (Postgres logado; localStorage
// anônimo/modo dev) e chega à simulação no início da partida.
// -----------------------------------------------------------------------------
export interface FeitosState {
  stats: FeatsStats;
  conquistas: string[];                   // ids de conquistas já cumpridas
  dossies: DossieState[];                 // as 3 pastas em cima da mesa
  nivel: Record<string, number>;          // template de dossiê -> vezes concluído
}

export function freshFeitosState(): FeitosState {
  return { stats: freshFeatsStats(), conquistas: [], dossies: freshDossies(), nivel: {} };
}

export function cloneFeitosState(state: FeitosState): FeitosState {
  return JSON.parse(JSON.stringify(state)) as FeitosState;
}

// Avalia TODAS as conquistas pendentes contra as estatísticas atuais (mutável).
// Retorna as recém-cumpridas; o chamador decide se credita a recompensa
// (crédito de fim de partida credita; migração retroativa não).
export function evaluateConquistas(state: FeitosState): { novas: ConquistaDef[]; votosBonus: number } {
  const novas: ConquistaDef[] = [];
  let votosBonus = 0;
  for (const c of CONQUISTA_LIST) {
    if (state.conquistas.includes(c.id)) continue;
    if (c.progress(state.stats) >= c.alvo) {
      state.conquistas.push(c.id);
      novas.push(c);
      votosBonus += c.votos;
    }
  }
  return { novas, votosBonus };
}

// Crédito completo de uma participação no perfil (mutável): funde estatísticas,
// avalia conquistas e avança dossiês. Usado pelo Db (logado), pelo MetaClient
// (anônimo, localStorage) e pela simulação (espelho local pro payload).
export interface FeitosCreditResult {
  votosBonus: number;
  conquistasNovas: ConquistaDef[];
  dossiesConcluidos: DossieConcluido[];
}

export function creditMatchToFeitos(state: FeitosState, m: MatchFeats): FeitosCreditResult {
  applyMatchToStats(state.stats, m);
  const conq = evaluateConquistas(state);
  const doss = applyMatchToDossies(state, m);
  return {
    votosBonus: conq.votosBonus + doss.votosBonus,
    conquistasNovas: conq.novas,
    dossiesConcluidos: doss.concluidos,
  };
}

// Anistia de migração: conta que JÁ jogava antes das travas por conquista não
// pode regredir de conteúdo — as conquistas que travam arma nascem cumpridas
// (sem recompensa retroativa em votos; a migração de votos já foi generosa).
export function anistiaArmas(state: FeitosState): void {
  for (const id of Object.values(CONQUISTA_ARMA_LOCKS)) {
    if (!state.conquistas.includes(id)) state.conquistas.push(id);
  }
}

// -----------------------------------------------------------------------------
// Sanitização — o estado do ANÔNIMO chega do navegador no join e do
// localStorage: só ids conhecidos, inteiros não-negativos, tamanhos clampados.
// -----------------------------------------------------------------------------
function nInt(v: unknown): number {
  const n = Math.floor(Number(v));
  return Number.isFinite(n) && n > 0 ? n : 0;
}

function nRec(raw: unknown, validKey: (k: string) => boolean): Record<string, number> {
  const out: Record<string, number> = {};
  if (typeof raw !== 'object' || raw === null) return out;
  for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
    if (!validKey(k)) continue;
    const n = nInt(v);
    if (n > 0) out[k] = n;
  }
  return out;
}

const isEnemyType = (k: string): boolean => ETYPE_IDS.includes(k);
const isBossType = (k: string): boolean => k in BOSSES;
// Armas: só ids do arsenal (o servidor grava sempre def.id de WEAPON_TABLE);
// chave desconhecida é lixo do cliente e seria estado residente re-serializado
// a cada segundo em checkConquistasLive — descarta na porta, como tipos/bosses.
const isArmaKey = (k: string): boolean => k in WEAPON_TABLE;

export function sanitizeFeatsStats(raw: unknown): FeatsStats {
  const r = (typeof raw === 'object' && raw !== null ? raw : {}) as Record<string, unknown>;
  const vistos = Array.isArray(r.vistos)
    ? [...new Set(r.vistos.filter((t): t is string => typeof t === 'string' && ETYPE_IDS.includes(t)))]
    : [];
  return {
    kills: nInt(r.kills),
    killsPorArma: nRec(r.killsPorArma, isArmaKey),
    killsPorTipo: nRec(r.killsPorTipo, isEnemyType),
    bosses: nRec(r.bosses, isBossType),
    maletas: nInt(r.maletas),
    revives: nInt(r.revives),
    zumbificacoes: nInt(r.zumbificacoes),
    congelados: nInt(r.congelados),
    evolucoes: nInt(r.evolucoes),
    votos: nInt(r.votos),
    tempo: nInt(r.tempo),
    partidas: nInt(r.partidas),
    vitorias: nInt(r.vitorias),
    vitoriasDificil: nInt(r.vitoriasDificil),
    melhorTempo: nInt(r.melhorTempo),
    melhorScore: nInt(r.melhorScore),
    melhorNivel: nInt(r.melhorNivel),
    infinitoTempo: nInt(r.infinitoTempo),
    vistos,
  };
}

function sanitizeDossies(raw: unknown, nivel: Record<string, number>): DossieState[] {
  const list: DossieState[] = [];
  if (Array.isArray(raw)) {
    for (const item of raw) {
      if (list.length >= DOSSIES_ATIVOS) break;
      const r = (typeof item === 'object' && item !== null ? item : {}) as Record<string, unknown>;
      const def = DOSSIE_TABLE[String(r.tpl)];
      if (!def || list.some((d) => d.tpl === def.id)) continue;
      const n = Math.min(nInt(r.nivel), 999);
      const alvo = dossieAlvo(def, n);
      list.push({ tpl: def.id, nivel: n, alvo, prog: Math.min(nInt(r.prog), alvo - 1) });
    }
  }
  // Mesa incompleta (estado novo/corrompido): completa com os templates de menor nível
  for (const def of DOSSIE_LIST) {
    if (list.length >= DOSSIES_ATIVOS) break;
    if (!list.some((d) => d.tpl === def.id)) list.push(novoDossie(def, nivel[def.id] ?? 0, 0));
  }
  return list;
}

export function sanitizeFeitosState(raw: unknown): FeitosState {
  const r = (typeof raw === 'object' && raw !== null ? raw : {}) as Record<string, unknown>;
  const conquistas = Array.isArray(r.conquistas)
    ? [...new Set(r.conquistas.filter((c): c is string => typeof c === 'string' && c in CONQUISTAS))]
    : [];
  const nivel = nRec(r.nivel, (k) => k in DOSSIES);
  return {
    stats: sanitizeFeatsStats(r.stats),
    conquistas,
    dossies: sanitizeDossies(r.dossies, nivel),
    nivel,
  };
}

// -----------------------------------------------------------------------------
// ARQUIVO DA CPI (#95): crimes atribuídos a cada investigado. Os bordões e os
// dados vêm de shared/enemies.ts; aqui mora só a peça de acusação.
// -----------------------------------------------------------------------------
export const CRIMES_CPI: Record<string, string> = {
  urna_possuida: 'Computou 14 milhões de votos para si mesma e negou em nota oficial redigida por ela própria.',
  santinho_voador: 'Poluição eleitoral em 5.570 municípios e desacato ao gari na manhã seguinte.',
  titulo_zumbi: 'Votou em nove eleições após o próprio velório; alega "erro de digitação do cartório".',
  biometria_fantasma: 'Furto de identidade em escala nacional; a digital pertencia a um dedo que ninguém encontrou.',
  robo_fake_news: 'Disparo em massa de mentira com selo de verdade; até hoje a fonte segue sendo "confia".',
  carro_de_som: 'Perturbação do sossego com jingle em loop; o refrão continua na sua cabeça até agora.',
  mala_de_dinheiro: 'Transporte de R$ 51 mil em espécie na cueca de terceiros; alega "biblioteca itinerante".',
  pesquisa_maldita: 'Margem de erro de 40 pontos; a amostra era o grupo de família do próprio instituto.',
  drone_fiscal: 'Voo rasante sem habilitação e multa aplicada em quem fez a denúncia.',
  trio_eletrico: 'Comício sem alvará, cachê pago por emenda e paredão acima do teto constitucional de decibéis.',
  live_19h: 'Receitou cloroquina em rede nacional e responsabilizou o coveiro pela queda de audiência.',
  mensalao2: 'Cobrança de mensalidade parlamentar com juros, correção monetária e reincidência premiada.',
  impeachment_dilma: 'Panelaço com dolo; o inquérito não concluiu se foi pedalada ou golpe — a resposta oficial é "sim".',
  cpi_covid: 'Transformou a pandemia em circo, o circo em palanque e pediu vista de si mesma 47 vezes.',
  stf_bolso: 'Decisões monocráticas contra todo mundo ao mesmo tempo; o motivo está sob sigilo de 100 anos.',
  orcamento_secreto: 'RP-9 sem relator, trator superfaturado e emenda apadrinhada por autor desconhecido.',
  lula_planalto: 'Prometeu picanha, entregou escândalo da PF de sobremesa e fez o PIB crescer só para a horda.',
  oito_jan_invasao: 'Depredação de patrimônio histórico, relógio de Dom João VI e excursão a Brasília sem nota fiscal.',
};

// Silhueta do investigado que ainda não prestou depoimento
export const CPI_PENDENTE = '??? — depoimento pendente';

// Só pra referência cruzada de quem consome o Arquivo (evita import duplo)
export { ENEMIES, BOSSES, ETYPE_IDS };
